import {
  Card,
  TextField,
  Button,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useEffect, useState } from 'react';
import BasicTable, { Row, BasicColumn } from '../FinancialPositions/components/BasicTable';

// Define task statuses
const TASK_STATUSES = ['Pending', 'In Progress', 'Delivered', 'Paid', 'Cancelled'] as const;
type TaskStatus = (typeof TASK_STATUSES)[number];

// Define task types
const TASK_TYPES = ['Purchase', 'Sale', 'Service', 'Other'] as const;
type TaskType = (typeof TASK_TYPES)[number];

// Task interface
interface Task {
  id: string;
  orderName: string;
  type: TaskType;
  created_at: string;
  status: TaskStatus;
  price: number;
  paid_amount: number;
  delivered_at?: string;
  paid_at?: string;
  in_progress_at?: string;
  cancelled_at?: string;
}

// Mock initial data
const MOCK_DATA: Task[] = [
  {
    id: '1',
    orderName: 'Order #1001',
    type: 'Purchase',
    created_at: '2025-10-01',
    status: 'Delivered',
    price: 1500,
    paid_amount: 1500,
    in_progress_at: '2025-10-02',
    delivered_at: '2025-10-05',
    paid_at: '2025-10-06',
  },
  {
    id: '2',
    orderName: 'Order #1002',
    type: 'Sale',
    created_at: '2025-10-10',
    status: 'In Progress',
    price: 2500,
    paid_amount: 0,
    in_progress_at: '2025-10-11',
  },
  {
    id: '3',
    orderName: 'Order #1003',
    type: 'Service',
    created_at: '2025-10-15',
    status: 'Pending',
    price: 800,
    paid_amount: 0,
  },
  {
    id: '4',
    orderName: 'Order #1004',
    type: 'Purchase',
    created_at: '2025-10-18',
    status: 'Paid',
    price: 3200,
    paid_amount: 3200,
    in_progress_at: '2025-10-19',
    delivered_at: '2025-10-20',
    paid_at: '2025-10-21',
  },
];

const LOCAL_STORAGE_KEY = 'taskManagerData';

export const TaskManager = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Form fields for new/edit task
  const [formData, setFormData] = useState<Partial<Task>>({
    orderName: '',
    type: 'Purchase',
    status: 'Pending',
    price: 0,
    paid_amount: 0,
  });

  // Load data from localStorage on mount
  useEffect(() => {
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        setTasks(parsed);
      } catch (error) {
        console.error('Error parsing localStorage data:', error);
        setTasks(MOCK_DATA);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(MOCK_DATA));
      }
    } else {
      // Initialize with mock data
      setTasks(MOCK_DATA);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(MOCK_DATA));
    }
  }, []);

  // Save to localStorage whenever tasks change
  useEffect(() => {
    if (tasks.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tasks));
    }
  }, [tasks]);

  // Generate timestamp in YYYY-MM-DD format
  const generateTimestamp = (): string => {
    return new Date().toISOString().split('T')[0];
  };

  // Add new task
  const handleAddTask = () => {
    const newTask: Task = {
      id: Date.now().toString(),
      orderName: formData.orderName || '',
      type: formData.type || 'Purchase',
      created_at: generateTimestamp(),
      status: formData.status || 'Pending',
      price: formData.price || 0,
      paid_amount: formData.paid_amount || 0,
    };

    setTasks([...tasks, newTask]);
    setShowAddDialog(false);
    resetForm();
  };

  // Update task status with timestamp
  const handleStatusChange = (taskId: string, newStatus: TaskStatus) => {
    setTasks(
      tasks.map((task) => {
        if (task.id === taskId) {
          const updatedTask = { ...task, status: newStatus };

          // Add timestamp based on status
          if (newStatus === 'In Progress' && !task.in_progress_at) {
            updatedTask.in_progress_at = generateTimestamp();
          } else if (newStatus === 'Delivered' && !task.delivered_at) {
            updatedTask.delivered_at = generateTimestamp();
          } else if (newStatus === 'Paid' && !task.paid_at) {
            updatedTask.paid_at = generateTimestamp();
          } else if (newStatus === 'Cancelled' && !task.cancelled_at) {
            updatedTask.cancelled_at = generateTimestamp();
          }

          return updatedTask;
        }
        return task;
      }),
    );
  };

  // Update task
  const handleUpdateTask = () => {
    if (!selectedTask) return;

    setTasks(
      tasks.map((task) => {
        if (task.id === selectedTask.id) {
          return {
            ...task,
            orderName: formData.orderName || task.orderName,
            type: formData.type || task.type,
            price: formData.price !== undefined ? formData.price : task.price,
            paid_amount: formData.paid_amount !== undefined ? formData.paid_amount : task.paid_amount,
          };
        }
        return task;
      }),
    );

    setShowEditDialog(false);
    setSelectedTask(null);
    resetForm();
  };

  // Delete task
  const handleDeleteTask = (taskId: string) => {
    if (confirm('Are you sure you want to delete this task?')) {
      setTasks(tasks.filter((task) => task.id !== taskId));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      orderName: '',
      type: 'Purchase',
      status: 'Pending',
      price: 0,
      paid_amount: 0,
    });
  };

  // Open edit dialog
  const handleEditClick = (task: Task) => {
    setSelectedTask(task);
    setFormData({
      orderName: task.orderName,
      type: task.type,
      price: task.price,
      paid_amount: task.paid_amount,
    });
    setShowEditDialog(true);
  };

  // Filter tasks by status
  const filteredTasks = filterStatus === 'All' ? tasks : tasks.filter((task) => task.status === filterStatus);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `$${amount.toLocaleString()}`;
  };

  // Column definitions with formatters
  const getColumns = (): BasicColumn[] => [
    {
      name: 'Order Name',
      value: 'orderName',
      width: 200,
      formatter: (data: Row) => (
        <div
          className="text-blue-600 cursor-pointer hover:underline"
          onClick={() => handleEditClick(data.row.original)}
        >
          {data?.value}
        </div>
      ),
    },
    {
      name: 'Type',
      value: 'type',
      width: 120,
    },
    {
      name: 'Created At',
      value: 'created_at',
      width: 150,
    },
    {
      name: 'Status',
      value: 'status',
      width: 180,
      formatter: (data: Row) => (
        <Select
          value={data?.value || 'Pending'}
          onChange={(e: SelectChangeEvent<string>) =>
            handleStatusChange(data.row.original.id, e.target.value as TaskStatus)
          }
          size="small"
          className="min-w-[140px]"
        >
          {TASK_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      name: 'Price',
      value: 'price',
      width: 120,
      formatter: (data: Row) => <span>{formatCurrency(data?.value || 0)}</span>,
    },
    {
      name: 'Paid Amount',
      value: 'paid_amount',
      width: 120,
      formatter: (data: Row) => <span>{formatCurrency(data?.value || 0)}</span>,
    },
    {
      name: 'In Progress At',
      value: 'in_progress_at',
      width: 150,
      formatter: (data: Row) => <span>{data?.value || '-'}</span>,
    },
    {
      name: 'Delivered At',
      value: 'delivered_at',
      width: 150,
      formatter: (data: Row) => <span>{data?.value || '-'}</span>,
    },
    {
      name: 'Paid At',
      value: 'paid_at',
      width: 150,
      formatter: (data: Row) => <span>{data?.value || '-'}</span>,
    },
    {
      name: 'Cancelled At',
      value: 'cancelled_at',
      width: 150,
      formatter: (data: Row) => <span>{data?.value || '-'}</span>,
    },
    {
      name: 'Actions',
      value: 'id',
      width: 120,
      formatter: (data: Row) => (
        <Button onClick={() => handleDeleteTask(data?.value)} variant="outlined" color="error" size="small">
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div className="w-full h-full bg-white flex flex-col p-4">
      <h1 className="text-3xl font-bold mb-4">Task Manager</h1>

      {/* Filter and Add Controls */}
      <Card className="flex flex-row gap-4 items-center min-h-16 mb-4 p-4">
        <div className="flex flex-col">
          <p className="text-xs mb-1">Filter by Status:</p>
          <Select
            value={filterStatus}
            onChange={(e: SelectChangeEvent<string>) => setFilterStatus(e.target.value)}
            className="min-w-[200px]"
          >
            <MenuItem value="All">All</MenuItem>
            {TASK_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="ml-auto">
          <Button onClick={() => setShowAddDialog(true)} variant="contained" color="primary">
            Add New Task
          </Button>
        </div>
      </Card>

      {/* Tasks Table */}
      <Card className="flex flex-col flex-1 p-4 overflow-auto">
        <div className="text-sm text-gray-600 mb-2">
          Total Tasks: {filteredTasks.length} {filterStatus !== 'All' && `(${tasks.length} total)`}
        </div>

        {filteredTasks.length > 0 ? (
          <div className="overflow-auto">
            <BasicTable
              allRows={filteredTasks}
              columns={getColumns()}
              options={{
                cellClass: 'p-2',
                noBorders: false,
              }}
            />
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center text-gray-500">No tasks found</div>
        )}
      </Card>

      {/* Add Task Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Task</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 pt-2">
            <TextField
              label="Order Name"
              value={formData.orderName}
              onChange={(e) => setFormData({ ...formData, orderName: e.target.value })}
              fullWidth
              required
            />

            <Select
              value={formData.type || 'Purchase'}
              onChange={(e: SelectChangeEvent<string>) =>
                setFormData({ ...formData, type: e.target.value as TaskType })
              }
              fullWidth
            >
              {TASK_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>

            <TextField
              label="Price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
            />

            <TextField
              label="Paid Amount"
              type="number"
              value={formData.paid_amount}
              onChange={(e) => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) || 0 })}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddDialog(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddTask} variant="contained" color="primary" disabled={!formData.orderName}>
            Add Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 pt-2">
            <TextField
              label="Order Name"
              value={formData.orderName}
              onChange={(e) => setFormData({ ...formData, orderName: e.target.value })}
              fullWidth
              required
            />

            <Select
              value={formData.type || 'Purchase'}
              onChange={(e: SelectChangeEvent<string>) =>
                setFormData({ ...formData, type: e.target.value as TaskType })
              }
              fullWidth
            >
              {TASK_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>

            <TextField
              label="Price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
            />

            <TextField
              label="Paid Amount"
              type="number"
              value={formData.paid_amount}
              onChange={(e) => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) || 0 })}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditDialog(false);
              setSelectedTask(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleUpdateTask} variant="contained" color="primary" disabled={!formData.orderName}>
            Update Task
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
